/*
  COMPONENT: Recently Viewed Products Runtime

  Owns:
  - Local recently viewed product history
  - Async product-card fetching from product pages

  Does not own:
  - Customer account history
*/

(() => {
  const storageKey = 'curtis:recently-viewed';
  const parser = new DOMParser();

  function readHistory() {
    try {
      return JSON.parse(window.localStorage.getItem(storageKey)) || [];
    } catch (error) {
      return [];
    }
  }

  function writeHistory(handles) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(handles.slice(0, 12)));
    } catch (error) {
      // Storage may be unavailable in private browsing or restricted contexts.
    }
  }

  function remember(handle) {
    if (!handle) return;
    const next = [handle, ...readHistory().filter((item) => item !== handle)];
    writeHistory(next);
  }

  async function fetchCard(handle, sectionId) {
    const url = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`;
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return '';

    const html = await response.text();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector('[data-recently-viewed-card-source]')?.innerHTML || '';
  }

  async function initializeSection(section) {
    if (section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';

    const currentHandle = section.dataset.productHandle;
    const sectionId = section.dataset.sectionId;
    const grid = section.querySelector('[data-recently-viewed-grid]');
    const limit = Number(section.dataset.limit || 4);
    const handles = readHistory().filter((handle) => handle && handle !== currentHandle).slice(0, limit);

    remember(currentHandle);

    if (!grid || !sectionId || !handles.length) {
      if (!grid?.children.length) section.classList.add('is-empty');
      return;
    }

    const cardResults = await Promise.allSettled(handles.map((handle) => fetchCard(handle, sectionId)));
    const html = cardResults
      .filter((result) => result.status === 'fulfilled' && result.value)
      .map((result) => result.value)
      .join('');

    if (html) {
      grid.innerHTML = html;
      window.CurtisWishlist?.sync(grid);
      window.CurtisTheme?.initialize(grid);
      window.CurtisMotion?.initialize(grid);
    } else if (!grid.children.length) {
      section.classList.add('is-empty');
    }
  }

  function initializeRecentlyViewed(root = document) {
    root.querySelectorAll('[data-recently-viewed-products]').forEach((section) => {
      if (section.dataset.observed === 'true' || section.dataset.initialized === 'true') return;

      const load = () => {
        initializeSection(section).catch(() => {
          if (!section.querySelector('[data-recently-viewed-grid]')?.children.length) section.classList.add('is-empty');
        });
      };

      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }

      section.dataset.observed = 'true';
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(section);
      window.CurtisTheme?.registerCleanup?.(section, () => observer.disconnect());
    });
  }

  initializeRecentlyViewed();
  document.addEventListener('shopify:section:load', (event) => initializeRecentlyViewed(event.target));
})();
